import type { TFunction } from 'i18next';
import { isGatewayOwnedInvoice, PaymentProvider, type InvoiceGatewayFields } from '@etiquetachef/types';

/**
 * Tradução das recusas "isto é do gateway" das callables de cobrança do admin,
 * e o aviso antecipado que impede o envio.
 *
 * Origem: #795 — 2026-10-03 18:22 +0200 (BIL-4 UI e BIL-5).
 *
 * Por quê: o servidor (commit b7f9f685c) passou a recusar baixa manual e
 * cancelamento de fatura que pertence ao Stripe/Asaas — mexer só no espelho
 * local deixava o gateway cobrando de novo (cobrança dupla) — e o
 * `cancelSubscriptionAdmin` a devolver `unavailable` quando o gateway não
 * confirma o cancelamento. A mensagem do servidor é só em português; o admin
 * traduz pelo `details.code`, que é o contrato estável.
 *
 * Contrato: puro (sem rede). `gatewayErrorMessage` devolve a mensagem
 * traduzida para um erro conhecido, ou `null` para o chamador cair no
 * fallback de sempre (`err.message`).
 *
 * Dados — erro do Firebase JS SDK (`FunctionsError`), campo `details` com o
 * terceiro argumento do `HttpsError` do servidor:
 *   recordManualPayment / cancelInvoice → `failed-precondition`
 *     { "code": "gateway_owned_invoice", "action": "record_payment" | "cancel",
 *       "provider": "stripe" | "asaas" | null, "externalInvoiceId": "in_000…" }
 *     (fonte: `apps/functions/src/admin/gatewayOwnedInvoice.ts`)
 *   cancelSubscriptionAdmin → `unavailable`
 *     { "code": "gateway_cancel_unconfirmed", "provider": "stripe" | "asaas",
 *       "livemode": true }
 *     (fonte: `apps/functions/src/billing/cancelSubscriptionAdmin.ts`)
 *   requestRefund → `failed-precondition`
 *     { "code": "asaas_refund_outcome_unknown", "refundId": "manual_…" }  (Asaas)
 *     { "code": "refund_outcome_unknown", "provider": "stripe", "refundId": "manual_…" }  (#871)
 *     (fonte: `apps/functions/src/billing/requestRefund.ts`)
 * `provider` pode vir `null` (doc legado só com `external_invoice_id`): aí o
 * texto fala em "gateway".
 *
 * Usado por: `RecordManualPaymentDialog`, `CancelInvoiceDialog`,
 * `CancelSubscriptionDialog` (admin web, `apps/admin/src/features/billing`);
 * `RequestRefundDialog` (`apps/admin/src/features/finance`).
 *
 * Arquitetura: a decisão "é do gateway?" é a MESMA função do servidor
 * (`isGatewayOwnedInvoice`, `@etiquetachef/types`) — o aviso antecipado e a
 * recusa não podem divergir. O servidor continua sendo quem recusa; o aviso é
 * só para não deixar o operador preencher um formulário que vai voltar.
 *
 * Dicas: chaves i18n em `billing.gatewayErrors.*`, nos 5 locales completos de
 * `packages/i18n/locales`.
 */

/** Código de app em `details.code` (espelho de `GATEWAY_OWNED_INVOICE_CODE` no servidor). */
export const GATEWAY_OWNED_INVOICE_CODE = 'gateway_owned_invoice';
/** Código de app em `details.code` (espelho de `GATEWAY_CANCEL_UNCONFIRMED_CODE` no servidor). */
export const GATEWAY_CANCEL_UNCONFIRMED_CODE = 'gateway_cancel_unconfirmed';

/**
 * Código de app em `details.code` (espelho de `REFUND_OUTCOME_UNKNOWN_CODE` em
 * `apps/functions/src/billing/requestRefund.ts`, #871): estorno anterior do
 * pagamento com desfecho desconhecido fora do Asaas.
 */
export const REFUND_OUTCOME_UNKNOWN_CODE = 'refund_outcome_unknown';

/** Nome do gateway para o texto; desconhecido/ausente → "gateway" traduzido. */
export function gatewayDisplayName(provider: unknown, t: TFunction): string {
  if (provider === PaymentProvider.STRIPE) return 'Stripe';
  if (provider === PaymentProvider.ASAAS) return 'Asaas';
  return t('billing.gatewayErrors.genericGateway');
}

/**
 * Aviso para mostrar ANTES do envio: a fatura é do gateway? Devolve o texto do
 * aviso da ação, ou `null` se a fatura é local (a ação pode seguir).
 *
 * Origem: #795 — 2026-10-03 18:22 +0200 (BIL-4).
 *
 * Arquitetura: usa `isGatewayOwnedInvoice` de `@etiquetachef/types`, a MESMA
 * função do servidor — o aviso e a recusa não podem divergir.
 *
 * Usado por: `RecordManualPaymentDialog` (`record_payment`) e
 * `CancelInvoiceDialog` (`cancel`), admin web.
 */
export function gatewayOwnedInvoiceWarning(
  invoice: InvoiceGatewayFields,
  action: 'record_payment' | 'cancel',
  t: TFunction,
): string | null {
  if (!isGatewayOwnedInvoice(invoice)) return null;
  const provider = gatewayDisplayName(invoice.external_provider, t);
  return action === 'record_payment'
    ? t('billing.gatewayErrors.ownedInvoicePayment', { provider })
    : t('billing.gatewayErrors.ownedInvoiceCancel', { provider });
}

/** `details` de um erro de callable, se for objeto. */
function detailsOf(err: unknown): Record<string, unknown> | null {
  if (typeof err !== 'object' || err === null) return null;
  const details = (err as { details?: unknown }).details;
  return typeof details === 'object' && details !== null ? (details as Record<string, unknown>) : null;
}

/**
 * Mensagem traduzida para as recusas conhecidas de gateway, ou `null` (o
 * chamador mostra `err.message`, como antes).
 *
 * Origem: #795 — 2026-10-03 18:22 +0200 (BIL-4 UI e BIL-5).
 *
 * Contrato: lê só `err.details.code`. Códigos tratados:
 * `gateway_owned_invoice`, `gateway_cancel_unconfirmed`,
 * `asaas_refund_outcome_unknown`, `refund_outcome_unknown`,
 * `invoice_already_paid`, `request_id_reused`, `amount_exceeds_balance`
 * (com `details.balance` em CENTAVOS + `details.currency` ISO). Formato de
 * cada `details` na docstring do módulo.
 *
 * Usado por: `RecordManualPaymentDialog`, `CancelInvoiceDialog`,
 * `CancelSubscriptionDialog` (`features/billing`) e `RequestRefundDialog`
 * (`features/finance`), admin web.
 *
 * Falhas conhecidas: 2026-10-07 (#795, auditoria do 1º nível) — sem os
 * códigos de estorno o operador reabria o diálogo e estornava em dobro;
 * #871 (Stripe, mesma trava); #872 (baixa acima do saldo).
 *
 * Histórico:
 *  - 2026-10-08 10:21 +0200 — (#871, #872) estorno Stripe e saldo excedido.
 *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
 */
export function gatewayErrorMessage(err: unknown, t: TFunction): string | null {
  const details = detailsOf(err);
  if (!details) return null;
  const provider = gatewayDisplayName(details.provider, t);
  if (details.code === GATEWAY_OWNED_INVOICE_CODE) {
    return details.action === 'cancel'
      ? t('billing.gatewayErrors.ownedInvoiceCancel', { provider })
      : t('billing.gatewayErrors.ownedInvoicePayment', { provider });
  }
  if (details.code === GATEWAY_CANCEL_UNCONFIRMED_CODE) {
    return t('billing.gatewayErrors.cancelUnconfirmed', { provider });
  }
  // #795 (auditoria do 1º nível, itens 2 e 3, 2026-10-07 16:20 +0200): sem
  // estas três, o operador via só `failed-precondition` — e no estorno do
  // Asaas o caminho natural era reabrir o diálogo e estornar em dobro.
  if (details.code === 'asaas_refund_outcome_unknown') {
    return t('billing.gatewayErrors.refundOutcomeUnknown', { provider: 'Asaas' });
  }
  // #871 (2026-10-08 10:21 +0200): a mesma trava passou a valer na Stripe —
  // timeout com o estorno aceito + diálogo reaberto estornava em dobro. O
  // servidor manda `refund_outcome_unknown` com o `provider` do estorno travado.
  if (details.code === REFUND_OUTCOME_UNKNOWN_CODE) {
    return t('billing.gatewayErrors.refundOutcomeUnknown', { provider });
  }
  if (details.code === 'invoice_already_paid') {
    return t('billing.gatewayErrors.invoiceAlreadyPaid');
  }
  if (details.code === 'request_id_reused') {
    return t('billing.gatewayErrors.requestIdReused');
  }
  // #872 (2026-10-08 10:21 +0200): baixa manual acima do saldo — outra aba ou
  // outro operador registrou parte/tudo antes. `details.balance` vem em
  // CENTAVOS e `details.currency` é o ISO da fatura (`recordManualPayment`).
  if (details.code === 'amount_exceeds_balance') {
    return t('billing.gatewayErrors.amountExceedsBalance', {
      balance: formatCents(details.balance, details.currency),
    });
  }
  return null;
}

/**
 * Centavos → texto de moeda para a recusa de saldo (#872). Valor ou moeda
 * fora do formato esperado viram `"?"`/só o número — a mensagem ainda sai,
 * só sem o valor bonito (o servidor é a fonte; isto é apresentação).
 */
function formatCents(cents: unknown, currency: unknown): string {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return '?';
  const units = cents / 100;
  if (typeof currency !== 'string') return units.toFixed(2);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(units);
  } catch {
    return `${currency} ${units.toFixed(2)}`;
  }
}
